import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Avatar,
  AvatarGroup,
  Button,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  AreaChart,
  Area,
  LineChart,
  Line,
} from "recharts";
import {
  TrendingUp,
  People,
  CalendarToday,
  Assignment,
  Event,
  Notifications,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  CheckCircle,
  AccessTime,
  School,
  EmojiEvents,
} from "@mui/icons-material";
import AppSnackbar from "../Components/Snackbar/snackbar";
import apiEndpoints from "../apiconfig";

const TeacherDashboard = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(true);
const [loading, setLoading] = useState(true);
const [dashboard, setDashboard] = useState(null);

useEffect(() => {
  const token = localStorage.getItem("token");

  fetch(apiEndpoints.teacherDashboard, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then(async (res) => {
      const text = await res.text();
      try {
        return text ? JSON.parse(text) : {};
      } catch (e) {
        return {};
      }
    })
    .then((res) => {
      if (res.status !== "success") return;
      setDashboard(res.data);
      setLoading(false);
    })
    .catch(() => setLoading(false));
}, []);

if (loading) {
  return <Typography>Loading dashboard...</Typography>;
}
  // ENHANCED TOP STATS WITH ICONS AND TRENDS
const {
  cards,
  attendance_trend,
  today_activities,
  subject_performance,
  attendance_donut,
  announcements_list,
  class_progress,
} = dashboard;

const stats = [
  {
    label: "My Students",
    value: cards.students,
    icon: <People sx={{ fontSize: 28 }} />,
    gradient: "linear-gradient(135deg,#667eea,#764ba2)",
  },
  {
    label: "Today's Attendance",
    value: cards.attendance_today,
    change: `${cards.attendance_percentage}%`,
    icon: <CalendarToday sx={{ fontSize: 28 }} />,
    gradient: "linear-gradient(135deg,#4facfe,#00f2fe)",
  },
  {
    label: "Announcements",
    value: cards.announcements,
    icon: <Assignment sx={{ fontSize: 28 }} />,
    gradient: "linear-gradient(135deg,#f093fb,#f5576c)",
  },
  {
    label: "Activities Planned",
    value: cards.activities_planned,
    icon: <Event sx={{ fontSize: 28 }} />,
    gradient: "linear-gradient(135deg,#43e97b,#38f9d7)",
  },
];

const attendanceChart = attendance_trend;
const activities = today_activities;
const overallAttendance = dashboard.overall_class_attendance;
const todayAttendance = attendance_donut;
const announcements = announcements_list;
const studentProgress = class_progress;


  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            boxShadow: 3,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{
                color: entry.color,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: entry.color,
                }}
              />
              {`${entry.name}: ${entry.value}${entry.unit || ""}`}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };



  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* HEADER SECTION */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: "linear-gradient(135deg, #3F51B5 0%, #2196F3 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Teacher Dashboard
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            Welcome back! Here's your classroom overview for today.
          </Typography>
        </Box>
      </Box>

      {/* ENHANCED STAT CARDS WITH PROPER SPACING */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex" }} key={index}>
            <Paper
              sx={{
                flex: 1,
                p: 3,
                borderRadius: 3,
                background: "rgba(255, 255, 255, 0.5)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.5)",
                boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ opacity: 0.9, fontWeight: 500, mb: 0.5 }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      mb: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {stat.trend === "up" && (
                      <ArrowUpward sx={{ fontSize: 16 }} />
                    )}
                    {stat.trend === "down" && (
                      <TrendingUp
                        sx={{ fontSize: 16, transform: "rotate(180deg)" }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.9, fontWeight: 500 }}
                    >
                      {stat.change}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: stat.gradient,
                    color: "#fff",
                  }}
                >
                  {stat.icon}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* MIDDLE SECTION - THREE CARDS IN PROPER ROW */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* TODAY'S ATTENDANCE DONUT CHART */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              height: "100%",
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Today's Attendance
              </Typography>
              <Chip
                label={`${cards.students} Students`}
                size="small"
                sx={{
                  bgcolor: "primary.lighter",
                  color: "primary.dark",
                  fontWeight: 600,
                }}
              />
            </Box>
            <Box sx={{ position: "relative", height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={todayAttendance}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={75}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {todayAttendance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} students`, ""]} />
                </PieChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: "primary.main" }}
                >
                  {cards.attendance_percentage}%
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Attendance Rate
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 3 }}>
              {todayAttendance.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "rgba(255, 255, 255, 0.5)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: item.color,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.name}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* ACTIVITIES PLANNED TODAY */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              height: "100%",
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Today's Activities
              </Typography>
              <Chip
                icon={<AccessTime />}
                label={`${cards.activities_planned} Activities`}
                size="small"
                color="primary"
              />
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ maxHeight: 320, overflow: "auto", pr: 1 }}>
              {activities.map((activity, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(255, 255, 255, 0.5)",
                    borderLeft: `4px solid ${
                      activity.status === "in-progress"
                        ? "#4CAF50"
                        : activity.status === "pending"
                        ? "#FF9800"
                        : "#9C27B0"
                    }`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "action.hover",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {activity.title}
                    </Typography>
                    <Chip
                      label={activity.time}
                      size="small"
                      sx={{
                        bgcolor: "primary.lighter",
                        color: "primary.dark",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Progress
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {activity.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={activity.progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "action.disabledBackground",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 4,
                          background: `linear-gradient(90deg, #3F51B5 0%, #2196F3 100%)`,
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <People sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {activity.students} students
                      </Typography>
                    </Box>
                    <Chip
                      label={activity.status}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontSize: "0.7rem",
                        height: 20,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* ANNOUNCEMENTS */}
        <Grid item xs={12} md={12} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              height: "100%",
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Announcements
              </Typography>
              <Chip
                label={`${cards.announcements} New`}
                size="small"
                color="info"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ maxHeight: 320, overflow: "auto", pr: 1 }}>
              {announcements_list.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No announcements available
                </Typography>
              ) : (
                announcements_list.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(255, 255, 255, 0.6)",
                      borderLeft: "4px solid #2196F3",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 1 }}
                    >
                      {item.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {item.created_date}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* BOTTOM CHARTS SECTION */}
      <Grid container spacing={3}>
        {/* STUDENT ATTENDANCE CHART */}
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              height: "100%",
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Student Attendance Trend
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Monthly attendance percentage per student
                </Typography>
              </Box>
              <Chip
                icon={<TrendingUp />}
                label="+5% Improvement"
                color="success"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.53rem", sm: "0.75rem", md: "0.8rem" },
                  px: { xs: 0, sm: 1.5, md: 2 },
                  height: { xs: 24, sm: 28, md: 32 },
                  "& .MuiChip-icon": {
                    fontSize: { xs: "0.7rem", sm: "1rem" },
                  },
                }}
              />
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attendanceChart}>
                <defs>
                  <linearGradient
                    id="attendanceGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#3F51B5" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3F51B5" stopOpacity={0.2} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="percent"
                  fill="url(#attendanceGradient)"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* PERFORMANCE RADAR CHART */}
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              height: "100%",
              background: "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.5)",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Overall Class Attendance
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Monthly attendance percentage for the entire class
                </Typography>
              </Box>
              <Chip
                icon={<EmojiEvents />}
                label="Top: Sports"
                color="info"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.53rem", sm: "0.75rem", md: "0.8rem" },
                  px: { xs: 1, sm: 1.5, md: 2 },
                  height: { xs: 24, sm: 28, md: 32 },
                  "& .MuiChip-icon": {
                    fontSize: { xs: "0.7rem", sm: "1rem" },
                  },
                }}
              />
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={overallAttendance}>
                <defs>
                  <linearGradient
                    id="classAttendanceGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.2} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(v) => [`${v}%`, "Attendance"]} />

                <Area
                  type="monotone"
                  dataKey="attendance"
                  stroke="#4CAF50"
                  fill="url(#classAttendanceGradient)"
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* CLASS PROGRESS TREND */}
      {/* <Paper
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 3,
          background: "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.5)",
          boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
          Class Progress Over Time
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={studentProgress}>
            <defs>
              <linearGradient id="progressGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#4CAF50" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="week" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="average"
              stroke="#4CAF50"
              fillOpacity={1}
              fill="url(#progressGradient)"
              strokeWidth={3}
            />
            <Line
              type="monotone"
              dataKey="average"
              stroke="#4CAF50"
              strokeWidth={3}
              dot={{ stroke: "#4CAF50", strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper> */}

      {/* Snackbar (uncomment when needed) */}
      {/* <AppSnackbar
        message="Welcome to Teacher Dashboard"
        severity="success"
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      /> */}
    </Box>
  );
};

export default TeacherDashboard;
